import { useMemo } from 'react'
import { ClientSideTable } from 'Presentation/Components/Table'
import { SmallCheckbox } from 'Presentation/Components/Checkbox'
import { useProjectStore } from 'Store/Project'
import { useTransferOut } from './useTransferOut'
import TransferOutModal from './TransferOutModal'
import ActionCell from './ActionCell'
import { useReturnDefective } from './useReturnDefective'

interface Props {
  projectId: string
  columns: any[]
  data: any[]
  onClickAdd: () => void
}

const AssetRequest: React.FC<Props> = ({ projectId, columns, data }) => {
  const setPiles = useProjectStore(state => state.setPiles)

  const {
    selectedIds,
    isModalOpen,
    isSubmitting,
    destination,
    setDestination,
    isSelectable,
    allEligibleSelected,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    openModal,
    closeModal,
    submitTransfer,
  } = useTransferOut({
    projectId,
    piles: data,
    setPiles,
  })

  const { isEligible, askReturnDefective } = useReturnDefective({
    projectId,
    piles: data,
    setPiles,
  })

  const tableColumns = useMemo(
    () => [
      {
        Header: () => (
          <SmallCheckbox
            value={allEligibleSelected && data.some(isSelectable)}
            onChange={toggleSelectAll}
          />
        ),
        accessor: 'checkbox',
        width: 50,
        minWidth: 50,
        Cell: ({ row }: any) => {
          const selectable = isSelectable(row.original)
          if (!selectable) {
            return (
              <div
                className='d-flex align-items-center opacity-50'
                title='เฉพาะเสาที่สถานะ received'>
                <input type='checkbox' className='form-check-input' disabled />
              </div>
            )
          }
          return (
            <SmallCheckbox
              value={selectedIds.has(String(row.original.id))}
              onChange={() => toggleSelect(row.original)}
            />
          )
        },
        disableSortBy: true,
      },
      ...columns,
      {
        Header: '',
        accessor: 'actions',
        width: 50,
        minWidth: 50,
        disableSortBy: true,
        Cell: ({ row }: any) =>
          isEligible(row.original) ? (
            <ActionCell row={row.original} onReturnDefective={askReturnDefective} />
          ) : null,
      },
    ],
    [
      allEligibleSelected,
      data,
      isSelectable,
      toggleSelectAll,
      selectedIds,
      toggleSelect,
      columns,
      isEligible,
      askReturnDefective,
    ]
  )

  return (
    <>
      <div className='card'>
        <div className='card-header'>
          <div className='card-title'></div>
          <div className='card-toolbar'></div>
        </div>

        {selectedIds.size > 0 && (
          <div className='mx-9 mt-4 p-4 bg-light-primary rounded d-flex flex-wrap align-items-center justify-content-between gap-3'>
            <span className='fw-bold text-gray-800'>เลือกแล้ว {selectedIds.size} ท่อน</span>
            <div className='d-flex gap-2'>
              <button type='button' className='btn btn-sm btn-light' onClick={clearSelection}>
                ยกเลิกการเลือก
              </button>
              <button type='button' className='btn btn-sm btn-primary' onClick={openModal}>
                ขนส่ง
              </button>
            </div>
          </div>
        )}

        <div className='card-body px-0'>
          <ClientSideTable
            isGrey
            pagination
            columns={tableColumns}
            data={data}
            items_per_page={10}
            sorting={[{ id: 'receivedToProjectAt', desc: true }]}
          />
        </div>
      </div>

      <TransferOutModal
        isOpen={isModalOpen}
        selectedCount={selectedIds.size}
        destination={destination}
        isSubmitting={isSubmitting}
        onDestinationChange={setDestination}
        onClose={closeModal}
        onConfirm={submitTransfer}
      />
    </>
  )
}

export default AssetRequest
